import { useMemo, useRef, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import MenuBubble from "./MenuBubble";
import Carousel from "./Carousel";
import "./Header.css";

// Import all images and videos from all subfolders
function importAll(r, type) {
  // Exclude any files from 'profile' folders
  return r.keys()
    .filter((file) => !file.toLowerCase().includes('profile'))
    .map((file) => ({
      type,
      src: r(file).default || r(file),
    }));
}
// Import all images from all subfolders in assets/images
const allImages = importAll(require.context('../assets/images', true, /\.(png|jpe?g|gif|PNG|JPG|JPEG|GIF)$/), 'image');
// Import all videos from all subfolders in assets/videos
const allVideos = importAll(require.context('../assets/videos', true, /\.(mp4|webm|ogg)$/), 'video');

/* --------------------------------------------------------------------------
   Carousel Index Helper
   Ensures carousel index wraps around correctly.
-------------------------------------------------------------------------- */
const getIndex = (i, length) => (i + length) % length;

/* --------------------------------------------------------------------------
   Header Component
   Displays the animated header with carousel and menu bubble.
   Handles expand/collapse, background transitions, and responsive behavior.
-------------------------------------------------------------------------- */
const Header = ({ collapsed, setCollapsed, imageDir = "home", videoDir = "home" }) => {
  // Refs for DOM elements
  const headerRef = useRef(null);
  const menuBubbleRef = useRef(null);

  // State for header visibility and animation
  const location = useLocation();
  const [showMenuBubble, setShowMenuBubble] = useState(true);
  const [contentVisible, setContentVisible] = useState(false);
  const [shrinking, setShrinking] = useState(false);
  useEffect(() => {
  setCollapsed(true);
  }, [location.pathname, setCollapsed]);
  /* ------------------------------------------------------------------------
     Show menu bubble on scroll/resize
  ------------------------------------------------------------------------ */
  useEffect(() => {
    function updateMenuBubble() {
      setShowMenuBubble(true);
    }
    window.addEventListener("scroll", updateMenuBubble);
    window.addEventListener("resize", updateMenuBubble);
    updateMenuBubble();
    return () => {
      window.removeEventListener("scroll", updateMenuBubble);
      window.removeEventListener("resize", updateMenuBubble);
    };
  }, []);

/* ------------------------------------------------------------------------
     Combine images and videos for carousel backgrounds (randomized)
  ------------------------------------------------------------------------ */
  // Shuffle array utility
  function shuffle(array) {
    let currentIndex = array.length, randomIndex;
    while (currentIndex !== 0) {
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;
      [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
    }
    return array;
  }
  // Exclude profile images and videos from the header gallery (robust path check)
  const filterProfile = (mediaArr) => mediaArr.filter(m => {
    const src = m.src.toLowerCase();
    return !src.includes('profile');
  });
  const backgrounds = useMemo(() => {
    return shuffle([...filterProfile(allImages), ...filterProfile(allVideos)]);
  }, []);


  const hasBackgrounds = backgrounds.length > 0;

  // Carousel index management
  const [index, setIndex] = useState(0);
  const prevIndex = getIndex(index - 1, backgrounds.length);
  const nextIndex = getIndex(index + 1, backgrounds.length);
  const farPrevIndex = getIndex(index - 2, backgrounds.length);
  const farNextIndex = getIndex(index + 2, backgrounds.length);

  /* ------------------------------------------------------------------------
     Renders a background image or video for the carousel
  ------------------------------------------------------------------------ */
  const renderBg = (bg, position, isCenter) => {
    if (!bg) return null;
    const uniqueKey = `${bg.src}-${position}`;
    if (bg.type === "image") {
      return (
        <img
          key={uniqueKey}
          src={bg.src}
          alt=""
          className={`carousel-item ${position}${isCenter ? " center" : ""}`}
        />
      );
    }
    if (bg.type === "video") {
      return (
        <video
          key={uniqueKey}
          src={bg.src}
          className={`carousel-item ${position}${isCenter ? " center" : ""}`}
          autoPlay
          loop
          muted
        />
      );
    }
    return null;
  };

  // Carousel navigation handlers
  const handlePrev = () => setIndex((prev) => getIndex(prev - 1, backgrounds.length));
  const handleNext = () => setIndex((prev) => getIndex(prev + 1, backgrounds.length));

  // Fade duration in ms (should match your CSS)
  const FADE_DURATION = 5000;

  /* ------------------------------------------------------------------------
     Render
     Shows either the floating expand button (when collapsed) or the header.
     Handles expand/collapse animation and passes backgrounds to Carousel.
  ------------------------------------------------------------------------ */
  return (
    <>
      {/* Header (may be collapsed or expanded) */}
      {collapsed ? (
        // Floating expand button (shows when header is collapsed)
        <button
          className="header-float-expand-btn"
          onClick={() => {
            setCollapsed(false);      // Show header (but keep shrinking)
            setShrinking(true);       // Start small and invisible
            setContentVisible(false); // Content invisible
            setTimeout(() => {
              setShrinking(false);    // Animate to full size and visible
              setContentVisible(true);
            }, 50); // Short delay to ensure DOM update
          }}
          aria-label="Expand gallery"
          title="Expand Gallery"
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="5" width="18" height="14" rx="2" fill="#fff" stroke="#5f1d7a" strokeWidth="2"/>
            <circle cx="8" cy="10" r="2" fill="#5f1d7a"/>
            <path d="M21 19l-5.5-7-4.5 6-3-4-4 5" stroke="#5f1d7a" strokeWidth="2" fill="none"/>
          </svg>
        </button>
      ) : (
        // Main header block with carousel and collapse button
        <header
          className={`header${collapsed ? " collapsed" : ""}${shrinking ? " shrink-out" : ""}`}
          ref={headerRef}
        >
          {/* Collapse button (top-right) */}
          <button
            className="header-collapse-btn"
            onClick={() => {
              setContentVisible(false); // Fade out content
              setShrinking(true);       // Shrink header
              setTimeout(() => {
                setShrinking(false);
                setCollapsed(true);     // Hide header after animation
              }, 700); // Match CSS transition duration
            }}
            aria-label="Collapse gallery"
          >
            X
          </button>
          {/* Carousel content (fades in/out) */}
          <div className={`header-content${contentVisible ? " visible" : ""}`}>
            <Carousel
              backgrounds={backgrounds}
              index={index}
              renderBg={renderBg}
              prevIndex={prevIndex}
              nextIndex={nextIndex}
              farPrevIndex={farPrevIndex}
              farNextIndex={farNextIndex}
              hasBackgrounds={hasBackgrounds}
              handlePrev={handlePrev}
              handleNext={handleNext}
            />
          </div>
        </header>
      )}
      {/* MenuBubble always rendered and always visible */}
      <MenuBubble
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        show={true} // <-- Always true!
        tabBubbleRef={menuBubbleRef}
      />
    </>
  );
};

export default Header;


